/**
 * The only permitted entry point into the wallet chunk.
 *
 * `vite.config.ts` puts `wagmi` and `viem` in their own rollup chunk so an
 * anonymous visitor never downloads them — §5.1's cold load has no wallet in it,
 * and Phase 4's first-paint criterion was measured without one. That separation
 * survives only as long as nothing *statically* imports `connectors.ts`: one
 * static import anywhere in the market-page graph pulls the chunk onto the
 * critical path for every visitor, and nothing about the page would look wrong.
 *
 * So the import lives here, behind `import()`, and `check-banned-names.mjs`
 * enforces that no other module imports `./connectors` directly.
 *
 * The chunk is loaded on the first *intent* to connect — the Connect button's
 * `pointerenter`, or its click — not on mount. Hovering is a strong enough
 * signal to prefetch and a weak enough one to cost nothing if wrong.
 */

import type { WalletConfig } from './connectors'
import { installBufferShim } from './buffer-shim'

let cached: Promise<WalletConfig> | null = null

/**
 * Load the wallet stack and build the wagmi config, once per tab.
 *
 * Idempotent, and deliberately a module-level singleton rather than React state:
 * two configs would mean two connector sets racing for the same injected
 * provider, and `StrictMode` double-mounts every effect in development, which is
 * exactly the shape of bug that produces.
 */
export function loadWalletConfig(): Promise<WalletConfig> {
  // **Before `./connectors`, not beside it.** `@coinbase/wallet-sdk` reads the
  // bare Node global `Buffer` on its signing path, and the SDK's module body
  // runs the moment that import resolves — so a shim installed in parallel
  // would be a race whose loser is a `ReferenceError` surfaced as an internal
  // RPC error from viem. `buffer-shim.ts` has the whole story.
  cached ??= installBufferShim()
    .then(() => import('./connectors'))
    .then(async (m) => {
      const config = m.createWalletConfig()

      // **Session restore.** Without this, a reload leaves the wagmi config
      // with no connection even though the wallet is still authorised and the
      // `ud_session` cookie is still valid — so the *server* thinks the user is
      // signed in, the header agrees, and the first action that needs a
      // signature fails with "Connect a wallet first."
      //
      // wagmi persists the last connector to `localStorage` but only
      // re-attaches when something asks it to; `<WagmiProvider>` would do this
      // via `reconnectOnMount`, and we deliberately do not mount one (see the
      // module comment). So the reconnect happens here, once, as part of
      // building the config — which keeps "the wallet is available" a single
      // await for every caller rather than a precondition each one has to
      // remember.
      //
      // Failure is non-fatal: a user who revoked the site in their wallet, or
      // switched browsers, simply lands back on Connect.
      try {
        const { reconnect } = await import('wagmi/actions')
        await reconnect(config)
      } catch {
        /* not previously connected, or the wallet declined to re-attach */
      }

      return config
    })
  return cached
}

/** Warm the chunk without building anything. Safe to call repeatedly. */
export function prefetchWalletChunk(): void {
  void loadWalletConfig().catch(() => {
    // A failed prefetch is not an error the user needs: the click path will
    // retry and surface it there, where there is somewhere to put a message.
  })
}

/** Tests only. */
export function __resetWalletConfig(): void {
  cached = null
}
